import { useMemo } from 'react'
import { Cable } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useRouterState } from '@tanstack/react-router'
import { parseMcpAuthorizationSearch } from '../domain/mcp-authorization'
import { useMcpAuthorization } from '../domain/use-mcp-authorization'
import { useCurrentLang } from '@/shared/i18n/use-current-lang'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

function isLoopbackCallback(redirectUrl: string): boolean {
  try {
    const { hostname } = new URL(redirectUrl)
    return ['127.0.0.1', 'localhost', '::1', '[::1]'].includes(hostname)
  } catch {
    return false
  }
}

async function deliverLoopbackCallback(redirectUrl: string): Promise<boolean> {
  if (!isLoopbackCallback(redirectUrl)) return false

  try {
    await fetch(redirectUrl, {
      method: 'GET',
      mode: 'no-cors',
      credentials: 'omit',
      cache: 'no-store',
    })
    return true
  } catch {
    return false
  }
}

export function McpAuthorizationDialog() {
  const { t } = useTranslation('settings')
  const navigate = useNavigate()
  const lang = useCurrentLang()
  const href = useRouterState({ select: (state) => state.location.href })
  const authorization = useMemo(
    () => parseMcpAuthorizationSearch(href, window.location.origin),
    [href],
  )
  const { approve, deny, pending, error, reset } = useMcpAuthorization()

  const complete = async (decision: 'approve' | 'deny') => {
    if (!authorization || pending) return
    reset()
    try {
      const result =
        decision === 'approve'
          ? await approve(authorization.request)
          : await deny(authorization.request)
      const delivered = await deliverLoopbackCallback(result.redirectUrl)
      if (!delivered) {
        window.location.assign(result.redirectUrl)
        return
      }

      await navigate({
        to: '/$lang/app',
        params: { lang },
        replace: true,
      })
    } catch {
      /* error state comes from the mutation */
    }
  }

  return (
    <Dialog
      open={Boolean(authorization)}
      onOpenChange={(open) => {
        if (!open) void complete('deny')
      }}
    >
      <DialogContent
        onEscapeKeyDown={(event) => {
          if (pending) event.preventDefault()
        }}
        onInteractOutside={(event) => {
          if (pending) event.preventDefault()
        }}
      >
        <DialogHeader>
          <div className="mb-2 flex size-10 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Cable className="size-5" />
          </div>
          <DialogTitle>
            {t('mcpAuthorizeTitle', {
              client: authorization?.clientName ?? 'MCP client',
            })}
          </DialogTitle>
          <DialogDescription>{t('mcpAuthorizeDescription')}</DialogDescription>
        </DialogHeader>
        {error ? (
          <p className="text-sm text-destructive" role="alert">
            {t('mcpAuthorizationFailed')}
          </p>
        ) : null}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            disabled={pending}
            onClick={() => void complete('deny')}
          >
            {t('mcpDeny')}
          </Button>
          <Button
            type="button"
            disabled={pending}
            onClick={() => void complete('approve')}
          >
            {t('mcpAuthorize')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
